import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';

import { customFrequencyUtils } from 'app-components/flux/scheduled-transactions';
import { categoriesSelectors } from 'app-components/flux/categories';
import { scheduledTransactionsUtils } from 'app-components/flux/scheduled-transactions';

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { getSmartDateLabel } from 'app-components/utils/date';
import { makeStyles } from 'tss-react/mui';

import BillPresentmentConnectBox from 'components/BillPresentmentConnectBox';
import ConnectedAvatar from 'components/ConnectedAvatar';
// CUSTOM
import StdDialog from 'components/Dialogs/StdDialog';
import QButton from 'components/QButton';
import QSkeleton from 'components/QSkeleton';
import AmountField, { ShowSignEnum } from 'components/QuickenControls/AmountField';
import { useAppSelector } from 'hooks/redux';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: '12px 0px 24px',
    height: 'fit-content',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'relative',
  },
  name: {
    width: '100%',
    padding: '0 24px',
    margin: '8px 0 14px',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  dotRow: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  amount: {
    fontWeight: 500,
    color: 'inherit',
  },
  frequency: {
    color: 'inherit',
  },
  dot: {
    height: 8,
    weight: 8,
  },
  editButton: {
    marginTop: 16,
    marginBottom: 12,
  },
}));

function ReviewDialog(props) {
  const { classes } = useStyles();
  const { model = {}, open = false, onClose, edit } = props;

  const txn = model.transaction || {};
  const incomeCoaIds = useAppSelector(categoriesSelectors.getIncomeCOAIds);
  const derivedIsIncome = scheduledTransactionsUtils.isIncomeSeriesFromTxn(txn, incomeCoaIds);

  const frequency = model && customFrequencyUtils.recurrenceRowAlias(model.recurrence);
  const modelNextDate = customFrequencyUtils
    .recurrenceInfo({ recurrence: model.recurrence, startDate: DateTime.fromISO(model.dueOn) })
    ?.nextDates[0]?.toISOString();
  const nextDue = modelNextDate ? getSmartDateLabel(modelNextDate) : null;

  const exist = Boolean(txn.payee);
  return (
    <StdDialog onClose={onClose} open={open} size='sm' title='New Recurring'>
      <div className={classes.root}>
        <ConnectedAvatar model={model} size={80} isCurrentCategoryIncome={derivedIsIncome} />
        {exist ? (
          <Typography variant='headlineRegular' className={classes.name}>
            {txn.payee}
          </Typography>
        ) : (
          <Skeleton variant='rectangular' width={240} height={34} className={classes.name} />
        )}

        {exist ? (
          <>
            <div className={classes.dotRow}>
              <AmountField
                showSign={ShowSignEnum.POSITIVE_ONLY}
                value={txn.amount}
                className={classes.amount}
                id='amount_id'
                variant='bodyMedium'
                editable={false}
              />

              <FiberManualRecordIcon className={classes.dot} />

              <Typography variant='bodyMedium' className={classes.frequency}>
                {frequency}
              </Typography>
            </div>
            {nextDue && (
              <Typography variant='bodyRegular' className={classes.frequency}>
                Next Reminder: {nextDue}
              </Typography>
            )}
          </>
        ) : (
          <Skeleton variant='rectangular' width={280} height={25} />
        )}

        <QSkeleton show={!exist}>
          <QButton variant='text' id='edit-series-button' className={classes.editButton} onClick={edit}>
            Edit Series
          </QButton>
        </QSkeleton>

        <QSkeleton show={!exist}>
          <BillPresentmentConnectBox model={model} onConnect={onClose} location='create series' />
        </QSkeleton>
      </div>
    </StdDialog>
  );
}

ReviewDialog.propTypes = {
  model: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  edit: PropTypes.func,
};

export default ReviewDialog;
